import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan } from '@fortawesome/pro-light-svg-icons';
import { useToast } from '@shared/hooks/use-toast';
import { getErrorMessage } from '@shared/utils/error.utils';
import { useBankAccountMutations } from '../../hooks/useBankAccountMutations';
import type { BankAccount } from '../../models/BankAccount';

export interface DisableBankAccountModalRef {
  open: (account: BankAccount) => void;
  close: () => void;
}

interface DisableBankAccountModalProps {
  onSuccess?: () => void;
}

export const DisableBankAccountModal = forwardRef<DisableBankAccountModalRef, DisableBankAccountModalProps>(
  ({ onSuccess }, ref) => {
    const { t } = useTranslation('bankAccountsModule');
    const { toast } = useToast();
    const modalRef = useRef<ModalCenterRef>(null);
    const { disableBankAccount } = useBankAccountMutations();

    const [account, setAccount] = useState<BankAccount | null>(null);

    useImperativeHandle(ref, () => ({
      open: (acc: BankAccount) => {
        setAccount(acc);
        modalRef.current?.openModal();
      },
      close: () => {
        modalRef.current?.closeModal();
      },
    }));

    const handleDisable = async () => {
      if (!account) return;

      try {
        await disableBankAccount.mutateAsync(account.id);

        toast({
          title: t('modals.disable.success.title'),
          description: t('modals.disable.success.description'),
        });

        modalRef.current?.closeModal();
        onSuccess?.();
      } catch (error) {
        toast({
          title: t('errors.disableFailed'),
          description: getErrorMessage(error),
          variant: 'destructive',
        });
      }
    };

    const isLoading = disableBankAccount.isPending;

    return (
      <ModalCenter ref={modalRef} title={t('modals.disable.title')} width="default">
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <FontAwesomeIcon icon={faBan} className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <p className="text-sm text-foreground">{t('modals.disable.description')}</p>
              <p className="text-sm text-amber-600 mt-2">⚠️ {t('modals.disable.warning')}</p>
            </div>
          </div>

          {account && (
            <div className="bg-muted rounded-lg p-3 space-y-1">
              <p className="text-sm">
                <span className="font-medium">Titular:</span> {account.holder_name}
              </p>
              <p className="text-sm font-mono">
                <span className="font-medium font-sans">IBAN:</span> {account.iban}
              </p>
              <p className="text-sm">
                <span className="font-medium">Banco:</span> {account.bank_name}
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => modalRef.current?.closeModal()} disabled={isLoading}>
              {t('buttons.cancel')}
            </Button>
            <Button variant="secondary" onClick={handleDisable} disabled={isLoading}>
              {isLoading ? t('buttons.disabling') : t('actions.disable')}
            </Button>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

DisableBankAccountModal.displayName = 'DisableBankAccountModal';
